import React, { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import MainPage from "./component/view/MainPage";
import DistrictPage from "./component/view/DistrictPage";
import Contact from "./component/view/Contact";
import HeadPage from "./component/view/HeadPage";
import About from "./component/view/About";
import debounce from "lodash.debounce";
import { createTheme } from "@mui/material";
// import MainRouteMueangOne from "./component/view/routes/route_mueang/MainRouteMueangOne";
// import MainRouteMueangTwo from "./component/view/routes/route_mueang/MainRouteMueangTwo";
// import MainRouteMueangThree from "./component/view/routes/route_mueang/MainRouteMueangThree";
// import MainRoutePhuPhaManOne from "./component/view/routes/route_phu_pha_man/MainRoutePhuPhaManOne";
// import MainRoutePhuPhaManTwo from "./component/view/routes/route_phu_pha_man/MainRoutePhuPhaManTwo";
// import MainRouteUbolratOne from "./component/view/routes/route_ubolrat/MainRouteUbolratOne";
// import MainRouteUbolratTwo from "./component/view/routes/route_ubolrat/MainRouteUbolratTwo";
import MainRoutes from "./component/view/routes/MainRoutes";
import { APIProvider } from "@vis.gl/react-google-maps";
import LoginPage from "./LoginPage";
import SignUpPage from "./SignUpPage";
import MainAdmin from "./component/admin/MainAdmin";
import MainUser from "./component/user/MainUser";
import "leaflet/dist/leaflet.css";
import TermofService from "./component/view/TermofService";
import PrivacyPolicy from "./component/view/PrivacyPolicy"
import PlacesList from "./component/view/places/PlacesList";
import PlacesDetails from "./component/view/places/PlacesDetails";
import MainTravelTypes from "./component/view/traveltypes/MainTravelTypes";
import ResetPasswordPage from "./ResetPasswordPage";
import MainGroupTourPage from "./component/view/grouptour/MainGroupTourPage";
import MainCreateTripPage from "./component/view/grouptour/MainJoinTripPage";
import MainJoinTripPage from "./component/view/grouptour/MainJoinTripPage";
import MainJoinTripPageDetail from "./component/view/grouptour/MainJoinTripPageDetail";
import GlobalLoadingOverlay from "./component/GlobalLoadingOverlay";
import { getRole } from "./component/auth";
import ScrollTopButton from "./component/ScrollTopButton";

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// อยู่นอก App เพื่อไม่ให้เป็น component type ใหม่ทุก render (ไม่งั้นหน้าลูก remount ทุกครั้งที่ App re-render)
const ProtectedRoute = ({ element, allowedRoles, userRole }) => {
  const currentRole = userRole ?? "guest";

  if (!allowedRoles.includes(currentRole)) {
    if (currentRole === "guest") return <Navigate to="/login" replace />;
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        ⛔ ไม่อนุญาตให้เข้าถึง
      </div>
    );
  }
  return element;
};

const defaultTheme = createTheme({
  typography: {
    fontFamily: '"Kanit", "Roboto", sans-serif',
  },
  palette: {
    primary: {
      main: "#007FFF",
      dark: "#0066CC",
    },
  },
});

export default function App() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");
  const [showHeader, setShowHeader] = useState(true);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  // อ่านใหม่ทุก render — App re-render ทุกครั้งที่เปลี่ยน route (useLocation) จึงอัปเดตหลัง login/logout
  const role = getRole();

  // ใช้ useRef เก็บสถานะล่าสุดของ showHeader เพื่อใช้ใน handler scroll
  const showHeaderRef = useRef(showHeader);

  // useEffect(() => {
  //   showHeaderRef.current = showHeader;
  // }, [showHeader]);

  // --app-vh = 1% ของความสูงจอที่มองเห็นจริง (หัก address bar ของ Safari/Chrome มือถือ-iPad แล้ว)
  // ใช้แทน vh: calc(var(--app-vh, 1vh) * 100) — ถ้า JS ยังไม่รัน จะ fallback เป็น 1vh แบบเดิม
  useEffect(() => {
    const setAppVh = () => {
      const h = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty("--app-vh", `${h * 0.01}px`);
    };
    setAppVh();
    window.addEventListener("resize", setAppVh);
    window.addEventListener("orientationchange", setAppVh);
    window.visualViewport?.addEventListener("resize", setAppVh);
    return () => {
      window.removeEventListener("resize", setAppVh);
      window.removeEventListener("orientationchange", setAppVh);
      window.visualViewport?.removeEventListener("resize", setAppVh);
    };
  }, []);

  useEffect(() => {
    let lastScrollTop = window.scrollY;

    // debounce ฟังก์ชันเพื่อจำกัดความถี่การเรียก setShowHeader
    const handleScroll = debounce(() => {
      const scrollTop = window.scrollY;

      if ((scrollTop < 100 || scrollTop < lastScrollTop) && !showHeaderRef.current) {
        setShowHeader(true);
      } else if (!(scrollTop < 100 || scrollTop < lastScrollTop) && showHeaderRef.current) {
        setShowHeader(false);
      }

      lastScrollTop = Math.max(scrollTop, 0);
    }, 100); // 100ms delay



    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      handleScroll.cancel(); // ยกเลิก debounce ตอน unmount
    };

  }, []);


  return (
      <APIProvider apiKey={apiKey}>
        <GlobalLoadingOverlay />
        {!isAdminPage && (
          // fixed top ตลอด + spacer สูงเท่า header (64px) ด้านล่าง เพื่อไม่ให้ทับเนื้อหาหน้าอื่น
          <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100 }}>
            <HeadPage
              screenWidth={screenWidth}
              defaultTheme={defaultTheme}
              roles={role}
              isAuthenticated={role !== "guest"}
            />
          </div>
          <div style={{ height: 64 }} />
          <ScrollTopButton />
          </>
        )}
        {/* BODY CONTENT */}
        <div>
          <Routes>
            <Route
              path="/"
              element={
                <MainPage screenWidth={screenWidth} defaultTheme={defaultTheme} />
              }
            />
            <Route
              path="/district/:id"
              element={
                <ProtectedRoute
                  element={
                    <DistrictPage
                      screenWidth={screenWidth}
                      defaultTheme={defaultTheme}
                    />
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/format/:ttid"
              element={
                <ProtectedRoute
                  element={
                    <MainTravelTypes
                      screenWidth={screenWidth}
                      defaultTheme={defaultTheme}
                    />
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/contact"
              element={
                <ProtectedRoute
                  element={
                    <Contact
                      screenWidth={screenWidth}
                      defaultTheme={defaultTheme}
                    />
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/tour/*"
              element={
                <ProtectedRoute
                  element={
                    <Routes>
                      {/* หน้าหลักทัวร์ */}
                      <Route
                        path=""
                        element={
                          <MainGroupTourPage
                            screenWidth={screenWidth}
                            defaultTheme={defaultTheme}
                          />
                        }
                      />
                      {/* หน้าสร้างทริป */}
                      <Route
                        path="join-trip"
                        element={
                          <ProtectedRoute
                            element={
                              <MainJoinTripPage
                                screenWidth={screenWidth}
                                defaultTheme={defaultTheme}
                              />
                            }
                            allowedRoles={["admin", "user", "guest"]}
                            userRole={role}
                          />
                        }
                      />
                      {/* หน้ารายละเอียดการเข้าร่วมทริป */}
                      <Route
                        path="join-trip/detail"
                        element={
                          <ProtectedRoute
                            element={
                              <MainJoinTripPageDetail
                                screenWidth={screenWidth}
                                defaultTheme={defaultTheme}
                              />
                            }
                            allowedRoles={["admin", "user", "guest"]}
                            userRole={role}
                          />
                        }
                      />
                    </Routes>
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/about"
              element={
                <ProtectedRoute
                  element={
                    <About
                      screenWidth={screenWidth}
                      defaultTheme={defaultTheme}
                    />
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/privacy"
              element={
                <ProtectedRoute
                  element={
                    <PrivacyPolicy
                      screenWidth={screenWidth}
                      defaultTheme={defaultTheme}
                    />
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/terms"
              element={
                <ProtectedRoute
                  element={
                    <TermofService
                      screenWidth={screenWidth}
                      defaultTheme={defaultTheme}
                    />
                  }
                  allowedRoles={["admin", "user", "guest"]}
                  userRole={role}
                />
              }
            />

            <Route
              path="/login"
              element={
                role !== "guest" ? (
                  <Navigate to="/" replace />
                ) : (
                  <LoginPage
                    screenWidth={screenWidth}
                    defaultTheme={defaultTheme}
                  />
                )
              }
            />
            <Route
              path="/reset-password"
              element={
                role !== "guest" ? (
                  <Navigate to="/" replace />
                ) : (
                  <ResetPasswordPage
                    screenWidth={screenWidth}
                    defaultTheme={defaultTheme}
                  />
                )
              }
            />

            <Route
              path="/signup"
              element={
                role !== "guest" ? (
                  <Navigate to="/" replace />
                ) : (
                  <SignUpPage
                    screenWidth={screenWidth}
                    defaultTheme={defaultTheme}
                  />
                )
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute
                  element={<MainAdmin screenWidth={screenWidth} defaultTheme={defaultTheme} />}
                  allowedRoles={["admin"]}
                  userRole={role}
                />
              }
            />
            <Route
              path="/user/*"
              element={
                <ProtectedRoute
                  element={<MainUser screenWidth={screenWidth} defaultTheme={defaultTheme} />}
                  allowedRoles={["user"]}
                  userRole={role}
                />
              }
            />

            <Route
              path="/place/*"
              element={
                <Routes>
                  {/* รายการแต่ละประเภท */}
                  <Route path=":placeType" element={<PlacesList />} />

                  {/* รายละเอียด */}
                  <Route path="/:placeType/:slug/:id" element={<PlacesDetails />} />
                </Routes>
              }
            />
            <Route
              path="/district/:slug/:id"
              element={
                  <MainRoutes />
              }
            />
          </Routes>
        </div>
      </APIProvider>
  );
}
